import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

export interface BillingRate {
  id: number;
  userId: string;
  caseTypeId: number | null;
  activityCodeId: number | null;
  billingTier: string | null;
  currency: string;
  hourlyRate: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  priority: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BillingRateInput {
  userId: string;
  caseTypeId?: number | null;
  activityCodeId?: number | null;
  billingTier?: string;
  currency?: string;
  hourlyRate: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  priority?: number;
}

export function useBillingRates(userId?: string) {
  return useQuery<BillingRate[]>({
    queryKey: ['billingRates', userId],
    queryFn: async () => {
      const { data } = await api.get('/api/billing-rates', {
        params: userId ? { userId } : {},
      });
      return data.items ?? [];
    },
    enabled: !!userId,
    staleTime: 2 * 60_000,
  });
}

export function useCreateBillingRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BillingRateInput) =>
      api.post('/api/billing-rates', body).then((r) => r.data.item),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['billingRates', vars.userId] });
    },
  });
}

export function useUpdateBillingRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      userId,
      ...body
    }: Partial<BillingRateInput> & { id: number; userId: string }) =>
      api.patch(`/api/billing-rates/${id}`, body).then((r) => r.data.item),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['billingRates', vars.userId] });
    },
  });
}
